/**
 * Tunables shared by the client and the Worker.
 *
 * Anything in `LIMITS` can be overridden per-deployment through `wrangler.jsonc`
 * vars. The defaults here are the Free-plan-safe values.
 */

export const PROTOCOL_VERSION = 1;

export const LIMITS = {
  /** Rolling conversation-context window handed to the translator. */
  MAX_CONTEXT_TURNS: 10,
  /** Hard cap on a single live session. */
  MAX_SESSION_SECONDS: 900,
  /** Largest single binary audio frame the client will send. */
  MAX_AUDIO_CHUNK_BYTES: 64 * 1024,
  /** Largest text payload accepted by the REST translation endpoint. */
  MAX_TEXT_CHARS: 4000,
  /** How often the pipeline emits an interim (partial) transcription. */
  AUDIO_PARTIAL_INTERVAL_MS: 900,
  /** Silence after which a segment is considered finished. */
  AUDIO_SILENCE_HOLD_MS: 700,
  /** A single segment is force-flushed at this length. */
  AUDIO_SEGMENT_MAX_MS: 12_000,
  /** REST text-translation calls per client per minute. */
  RATE_LIMIT_TEXT_PER_MINUTE: 30,
  /** Audio seconds a single session may consume. */
  RATE_LIMIT_AUDIO_SECONDS_PER_SESSION: 1800,
  /** Daily Workers AI neuron budget across the deployment. */
  DAILY_AI_UNIT_BUDGET: 200_000,
  /** Ceiling for `translation.request` batch size. */
  MAX_BATCH_SIZE: 20,
  /** Websocket heartbeat interval. */
  HEARTBEAT_MS: 20_000,
  /** Reconnect backoff bounds. */
  RECONNECT_MIN_MS: 600,
  RECONNECT_MAX_MS: 15_000,
  /** Idle session auto-teardown. */
  SESSION_IDLE_TIMEOUT_MS: 120_000,
} as const;

/** Client-side audio format. Chosen for Whisper/Deepgram compatibility and CPU cost. */
export const AUDIO = {
  /** Capture rate. 16 kHz mono is the sweet spot for ASR quality per byte. */
  SAMPLE_RATE: 16_000,
  CHANNELS: 1,
  /** Frame size handed to the capture worklet. */
  FRAME_SAMPLES: 2048,
  /** Client-side resampling ratio from the device rate down to SAMPLE_RATE. */
  get RATIO(): number {
    return 1;
  },
  /** Below this RMS the frame counts as silence. */
  SILENCE_RMS_THRESHOLD: 0.012,
  /** Consecutive silent frames that open a segment. */
  SILENCE_FRAMES_TO_SEGMENT: 3,
  /** Smoothing applied to the RMS envelope. */
  RMS_ATTACK: 0.35,
  RMS_RELEASE: 0.08,
} as const;

/** Web audio byte format. Linear PCM 16-bit little-endian mono. */
export const PCM = {
  BITS_PER_SAMPLE: 16,
  BYTES_PER_SAMPLE: 2,
} as const;

/** Approximate bytes per second of linear16 mono audio at `AUDIO.SAMPLE_RATE`. */
export const BYTES_PER_SECOND = AUDIO.SAMPLE_RATE * PCM.BYTES_PER_SAMPLE;

export const SPEAKER_IDS = ["A", "B", "C", "D"] as const;
export type SpeakerId = (typeof SPEAKER_IDS)[number];

export const CONVERSATION_MODES = ["solo", "two-way"] as const;
export type ConversationMode = (typeof CONVERSATION_MODES)[number];

/** What the browser should do with translated audio. */
export const AUDIO_OUTPUT_MODES = ["off", "translated", "both"] as const;
export type AudioOutputMode = (typeof AUDIO_OUTPUT_MODES)[number];

export const APP_MODES = ["text", "voice", "conversation", "subtitles"] as const;
export type AppMode = (typeof APP_MODES)[number];

/** Connection states surfaced in the UI status pill. */
export const SESSION_STATES = [
  "idle",
  "connecting",
  "connected",
  "listening",
  "processing",
  "translating",
  "speaking",
  "paused",
  "error",
  "closed",
] as const;
export type SessionState = (typeof SESSION_STATES)[number];

/** Terminal-ish states that should stop auto-reconnect. */
export const TERMINAL_STATES: ReadonlySet<SessionState> = new Set(["error", "closed"]);

export const STORAGE_KEYS = {
  settings: "dream-tongue:settings:v1",
  history: "dream-tongue:history:v1",
  session: "dream-tongue:session:v1",
} as const;

export const ERROR_CODES = [
  "bad_request",
  "method_not_allowed",
  "unauthorized",
  "forbidden",
  "not_found",
  "rate_limited",
  "quota_exceeded",
  "payload_too_large",
  "unsupported_media_type",
  "provider_unavailable",
  "model_unavailable",
  "language_unsupported",
  "session_expired",
  "timeout",
  "network",
  "internal",
  "cancelled",
] as const;
export type ErrorCode = (typeof ERROR_CODES)[number];

/** HTTP status for each error code. */
export const ERROR_STATUS: Record<ErrorCode, number> = {
  bad_request: 400,
  method_not_allowed: 405,
  unauthorized: 401,
  forbidden: 403,
  not_found: 404,
  rate_limited: 429,
  quota_exceeded: 429,
  payload_too_large: 413,
  unsupported_media_type: 415,
  provider_unavailable: 502,
  model_unavailable: 503,
  language_unsupported: 422,
  session_expired: 410,
  timeout: 504,
  network: 502,
  internal: 500,
  cancelled: 499,
};
